import { useState } from "react";

import { LabelInputWithDelete } from "../../../components/Reusable/Layout";
import Modal from "../../../components/Reusable/Modal";
import { Button } from "../../../components/Reusable/Button";
import { SubCategoriesModalProps, SubCategory } from "./types";

const SubCategoriesModal: React.FC<SubCategoriesModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [subcategories, setSubcategories] = useState<SubCategory[]>([
    { id: "1", value: "" },
    { id: "2", value: "" },
  ]);

  const handleChange = (id: string, value: string) => {
    setSubcategories((current) =>
      current.map((item) => (item.id === id ? { ...item, value } : item))
    );
  };

  const handleDelete = (id: string) => {
    // Don't allow deletion if there are only 2 fields
    if (subcategories.length <= 2) return;

    setSubcategories((current) => current.filter((item) => item.id !== id));
  };

  const addAnother = () => {
    const newId = String(Date.now());
    setSubcategories((current) => [...current, { id: newId, value: "" }]);
  };

  const handleCreate = () => {
    const validSubcategories = subcategories
      .map((item) => item.value.trim())
      .filter((value) => value !== "");

    if (validSubcategories.length > 0) {
      onCreate(validSubcategories);
      setSubcategories([
        { id: "1", value: "" },
        { id: "2", value: "" },
      ]);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white dark:bg-[#1E1E1E] rounded-lg p-6 w-full max-w-md shadow-2xl"
        style={{
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
        onClick={handleModalClick}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold dark:text-white">
            Create subcategories
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4">
          {subcategories.map((item, index) => {
            let placeholder =
              index === 0
                ? "Airtime"
                : index === 1
                ? "Data"
                : subcategories[index - 1].value || `Subcategory ${index + 1}`;

            return (
              <div key={item.id} className="flex items-center gap-2">
                <div className="flex-1">
                  <LabelInputWithDelete
                    type="text"
                    value={item.value}
                    placeholder={placeholder}
                    name={`subcategory-${item.id}`}
                    onChange={(e) => handleChange(item.id, e.target.value)}
                    onDelete={
                      subcategories.length > 2
                        ? () => handleDelete(item.id)
                        : undefined
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            className="flex items-center mt-2 gap-2 border border-[#D0D5DD] w-[149px] h-[36px] rounded"
            onClick={addAnother}
          >
            <span className="ml-2">+</span>
            <p>Add another</p>
          </button>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            onClick={handleCreate}
            type="submit"
            bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
            textColor="text-white"
            borderRadius="rounded-lg"
            className="w-[200px] h-10"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubCategoriesModal;
